(() => {
  const RED_DINGO_SKUS = new Set([
    '01-BN-BB-SM', '01-BN-RE-SM', '01-PP-BB-SM', '01-PP-RE-SM',
    '01-BN-BB-ME', '01-BN-DB-ME', '01-BN-GR-ME', '01-BN-HP-ME',
    '01-BN-OR-ME', '01-BN-PU-ME', '01-BN-RE-ME', '01-CG-GR-ME',
    '01-CP-PK-ME', '01-DA-GR-ME', '01-DA-HP-ME', '01-PP-BB-ME',
    '01-PP-DB-ME', '01-PP-GR-ME', '01-PP-HP-ME', '01-PP-PU-ME',
    '01-PP-RE-ME', '01-UK-DB-ME', '01-XB-BB-ME', '01-XB-RE-ME',
    '01-BN-BB-LG', '01-BN-DB-LG', '01-BN-RE-LG', '01-CG-GR-LG',
    '01-PP-BB-LG', '01-PP-RE-LG', '02-BN-ZZ-ME',
  ]);

  const ATTRIBUTE_KEYS = new Set([
    'Engraving',
    'Font',
    'Engraving side',
    'Engraving sides',
    'Side 1 - Line 1',
    'Side 1 - Line 2',
    'Side 1 - Line 3',
    'Side 2 - Line 1',
    'Side 2 - Line 2',
    'Engraving details confirmed',
  ]);

  const itemSku = (item) => item?.sku || item?.variant?.sku || item?.product?.sku || '';

  const attributeMap = (item) => {
    const attributes = item?.customAttributes || item?.custom_attributes || item?.attributes || [];
    if (Array.isArray(attributes)) {
      return new Map(
        attributes
          .map((attribute) => [attribute?.key || attribute?.name, attribute?.value])
          .filter(([key]) => Boolean(key))
      );
    }
    if (attributes && typeof attributes === 'object') return new Map(Object.entries(attributes));
    return new Map();
  };

  const sparkCartItems = (cart) => {
    const candidates = [
      cart?.products,
      cart?.items,
      cart?.lineItems,
      cart?.lines,
      cart?.cart?.products,
      cart?.cart?.items,
      cart?.cart?.lineItems,
      cart?.cart?.lines,
    ];
    return candidates.find((candidate) => Array.isArray(candidate)) || [];
  };

  const validateSparkCheckout = (cart) => {
    const invalidItems = sparkCartItems(cart).filter((item) => {
      const sku = itemSku(item);
      if (!RED_DINGO_SKUS.has(sku)) return false;

      const attributes = attributeMap(item);
      const engraving = attributes.get('Engraving');
      if (engraving === 'Blank tag') return false;
      if (engraving !== 'Engraved') return true;

      if (attributes.get('Font') !== 'Optima') return true;
      if (!String(attributes.get('Side 1 - Line 1') || '').trim()) return true;
      if (attributes.get('Engraving details confirmed') !== 'Yes') return true;
      if (
        sku.startsWith('02-') &&
        attributes.get('Engraving sides') === 'Two sides' &&
        !String(attributes.get('Side 2 - Line 1') || '').trim()
      ) {
        return true;
      }
      return false;
    });

    if (!invalidItems.length) return null;
    return [
      {
        message:
          'One or more Red Dingo tags is missing complete engraving details. Return to the product page and add each personalised tag again.',
      },
    ];
  };

  const applySparkAttributes = (cart) => {
    if (!cart?.products?.length || typeof document === 'undefined') return cart;

    const wrappers = Array.from(
      document.querySelectorAll('red-dingo-engraving[data-mode="spark"]')
    );
    if (!wrappers.length) return cart;

    const invalidSkus = new Set();
    cart.products.forEach((item) => {
      const sku = itemSku(item);
      if (!RED_DINGO_SKUS.has(sku)) return;

      const wrapper = wrappers.find((candidate) => candidate.hasSku?.(sku));
      if (!wrapper) return;
      wrapper.setVariantBySku?.(sku);

      if (!wrapper.validateForSpark?.()) {
        invalidSkus.add(sku);
        return;
      }

      const newAttributes = wrapper.collectAttributes?.() || [];
      const existingAttributes = Array.isArray(item.customAttributes) ? item.customAttributes : [];
      item.customAttributes = existingAttributes
        .filter((attribute) => !ATTRIBUTE_KEYS.has(attribute?.key || attribute?.name))
        .concat(newAttributes);
    });

    if (!invalidSkus.size) return cart;
    return {
      ...cart,
      products: cart.products.filter((item) => !invalidSkus.has(itemSku(item))),
    };
  };

  window.RedDingoTag = {
    skus: RED_DINGO_SKUS,
    applySparkAttributes,
    validateSparkCheckout,
  };

  if (typeof HTMLElement === 'undefined' || typeof customElements === 'undefined') return;
  if (customElements.get('red-dingo-engraving')) return;

  class RedDingoEngraving extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.mode = this.dataset.mode || 'shopify';
      this.choice = this.querySelector('[data-rd-choice]');
      this.details = this.querySelector('[data-rd-details]');
      this.sides = this.querySelector('[data-rd-sides]');
      this.sidesGroup = this.querySelector('[data-rd-sides-group]');
      this.sideTwo = this.querySelector('[data-rd-side="2"]');
      this.fontField = this.querySelector('[data-rd-font]');
      this.enamelSideField = this.querySelector('[data-rd-enamel-side]');
      this.confirmation = this.querySelector('[data-rd-confirmation]');
      this.error = this.querySelector('[data-rd-error]');
      this.limit = this.querySelector('[data-rd-limit]');
      this.smallNote = this.querySelector('[data-rd-small-note]');
      this.preview = this.querySelector('[data-rd-preview]');
      this.previewImage = this.querySelector('[data-rd-preview-image]');
      this.previewText = this.querySelector('[data-rd-preview-text]');
      this.previewEmpty = this.querySelector('[data-rd-preview-empty]');
      this.previewSideLabel = this.querySelector('[data-rd-preview-side-label]');
      this.previewSideControls = this.querySelector('[data-rd-preview-side-controls]');
      this.previewLines = Array.from(this.querySelectorAll('[data-rd-preview-line]'));
      this.previewSideButtons = Array.from(this.querySelectorAll('[data-rd-preview-side-button]'));
      this.dialog = this.querySelector('[data-rd-dialog]');
      this.openButton = this.querySelector('[data-rd-open]');
      this.openLabel = this.querySelector('[data-rd-open-label]');
      this.closeButtons = Array.from(this.querySelectorAll('[data-rd-close]'));
      this.saveButton = this.querySelector('[data-rd-save]');
      this.launchStatus = this.querySelector('[data-rd-launch-status]');
      this.personalisationSaved = false;
      this.previewSide = 1;
      this.form = this.closest('form');

      try {
        this.variants = JSON.parse(this.querySelector('[data-rd-variants]')?.textContent || '{}');
      } catch (error) {
        console.error('Unable to read Red Dingo variant limits.', error);
        this.variants = {};
      }

      this.boundRefresh = () => {
        this.personalisationSaved = false;
        this.updateLauncherStatus();
        this.refreshFields();
      };
      this.boundSubmit = (event) => this.handleSubmit(event);
      this.boundOpen = () => this.openDialog();
      this.boundClose = () => this.closeDialog();
      this.boundSave = () => this.savePersonalisation();
      this.boundConfirmationChange = () => {
        this.personalisationSaved = false;
        this.updateLauncherStatus();
      };
      this.boundDialogClose = () => {
        document.documentElement.classList.remove('red-dingo-modal-open');
      };
      this.boundDialogClick = (event) => {
        if (event.target === this.dialog) this.closeDialog();
      };
      this.boundFormChange = (event) => {
        if (event.target?.name === 'id' || event.target?.classList?.contains('product-variant-id')) {
          this.setVariant(event.target.value);
        }
      };
      this.boundSparkVariant = (event) => this.handleSparkVariant(event);
      this.boundPreviewSide = (event) => {
        const side = Number(event.currentTarget?.dataset.rdPreviewSideButton);
        if (![1, 2].includes(side)) return;
        event.preventDefault();
        this.previewSide = side;
        this.updatePreview();
      };

      this.choice?.addEventListener('change', this.boundRefresh);
      this.sides?.addEventListener('change', this.boundRefresh);
      this.confirmation?.addEventListener('change', this.boundConfirmationChange);
      this.openButton?.addEventListener('click', this.boundOpen);
      this.closeButtons.forEach((button) => button.addEventListener('click', this.boundClose));
      this.saveButton?.addEventListener('click', this.boundSave);
      this.dialog?.addEventListener('close', this.boundDialogClose);
      this.dialog?.addEventListener('click', this.boundDialogClick);
      this.previewSideButtons.forEach((button) => button.addEventListener('click', this.boundPreviewSide));
      this.form?.addEventListener('submit', this.boundSubmit, true);
      this.form?.addEventListener('change', this.boundFormChange);

      this.querySelectorAll('[data-rd-uppercase]').forEach((input) => {
        input.addEventListener('input', () => {
          const caretStart = input.selectionStart;
          const caretEnd = input.selectionEnd;
          input.value = input.value.toUpperCase();
          if (caretStart !== null && caretEnd !== null) input.setSelectionRange(caretStart, caretEnd);
          this.personalisationSaved = false;
          this.updateLauncherStatus();
          this.updateCounter(input);
          this.updatePreview();
        });
      });

      if (
        typeof subscribe === 'function' &&
        typeof PUB_SUB_EVENTS !== 'undefined' &&
        PUB_SUB_EVENTS.variantChange
      ) {
        this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (data?.variant?.id && this.variants[String(data.variant.id)]) {
            this.setVariant(data.variant.id);
          }
        });
      }

      this.setVariant(this.dataset.initialVariantId);
      this.updateLauncherStatus();
      if (this.mode === 'spark') {
        requestAnimationFrame(() => this.attachSparkPdp());
        window.setTimeout(() => this.attachSparkPdp(), 750);
      }
    }

    disconnectedCallback() {
      this.choice?.removeEventListener('change', this.boundRefresh);
      this.sides?.removeEventListener('change', this.boundRefresh);
      this.confirmation?.removeEventListener('change', this.boundConfirmationChange);
      this.openButton?.removeEventListener('click', this.boundOpen);
      this.closeButtons?.forEach((button) => button.removeEventListener('click', this.boundClose));
      this.saveButton?.removeEventListener('click', this.boundSave);
      this.dialog?.removeEventListener('close', this.boundDialogClose);
      this.dialog?.removeEventListener('click', this.boundDialogClick);
      this.previewSideButtons?.forEach((button) => button.removeEventListener('click', this.boundPreviewSide));
      this.form?.removeEventListener('submit', this.boundSubmit, true);
      this.form?.removeEventListener('change', this.boundFormChange);
      this.sparkPdp?.removeEventListener('spark-variant-change', this.boundSparkVariant);
      this.unsubscribeVariant?.();
      document.documentElement.classList.remove('red-dingo-modal-open');
    }

    attachSparkPdp() {
      const sparkPdp = this.closest('product-info')?.querySelector('spark-pdp');
      if (!sparkPdp || sparkPdp === this.sparkPdp) return;
      this.sparkPdp?.removeEventListener('spark-variant-change', this.boundSparkVariant);
      this.sparkPdp = sparkPdp;
      this.sparkPdp.addEventListener('spark-variant-change', this.boundSparkVariant);
    }

    handleSparkVariant(event) {
      const detail = event.detail || event.data || {};
      const externalId = detail?.variant?.externalId || detail?.variant?.externalID;
      const sku = detail?.variant?.sku;
      if (externalId && this.variants[String(externalId)]) {
        this.setVariant(externalId);
      } else if (sku) {
        this.setVariantBySku(sku);
      }
    }

    hasSku(sku) {
      return Object.values(this.variants).some((variant) => variant.sku === sku);
    }

    setVariantBySku(sku) {
      const entry = Object.entries(this.variants).find(([, variant]) => variant.sku === sku);
      if (entry) this.setVariant(entry[0]);
    }

    setVariant(variantId) {
      const config = this.variants[String(variantId)] || Object.values(this.variants)[0];
      if (!config) return;
      const previousSku = this.dataset.selectedSku;
      this.config = config;
      this.dataset.selectedSku = config.sku;

      if (previousSku && previousSku !== config.sku) {
        this.personalisationSaved = false;
        this.updateLauncherStatus();
      }

      if (this.preview) {
        this.preview.dataset.previewSize = config.previewSize || 'medium';
        this.preview.dataset.lineCount = String(config.lines || 2);
      }
      if (this.previewImage) {
        const sourceKey = `rdPreviewSrc${String(config.previewSize || 'medium')
          .split('-')
          .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
          .join('')}`;
        const source = this.previewImage.dataset[sourceKey];
        if (source && this.previewImage.src !== source) this.previewImage.src = source;
        const wideFlatPreview = config.previewSize === 'flat-bone';
        const flatPreview = wideFlatPreview || config.previewSize === 'flat-circle';
        this.previewImage.width = wideFlatPreview ? 1500 : 1200;
        this.previewImage.height = wideFlatPreview ? 965 : 1200;
        this.previewImage.alt = config.previewSize === 'flat-circle'
          ? 'Blank flat stainless-steel Circle tag'
          : flatPreview
            ? 'Blank flat stainless-steel Bone tag'
            : `Blank ${config.previewSize || 'medium'} stainless-steel tag reverse`;
      }

      if (this.limit) {
        this.limit.textContent = config.flat
          ? `Up to ${config.lines} lines of ${config.characters} characters per side.`
          : `Up to ${config.lines} lines of ${config.characters} characters on the stainless-steel reverse.`;
      }
      if (this.smallNote) this.smallNote.hidden = !config.small;
      this.refreshFields();
    }

    refreshFields() {
      if (!this.config || !this.choice) return;
      const engraved = this.choice.value === 'Engraved';
      const flat = Boolean(this.config.flat);
      const twoSides = flat && this.sides?.value === 'Two sides';

      if (!twoSides) this.previewSide = 1;

      this.details.hidden = !engraved;
      this.sidesGroup.hidden = !engraved || !flat;
      this.sideTwo.hidden = !engraved || !twoSides;

      this.fontField.disabled = !engraved;
      this.enamelSideField.disabled = !engraved || flat;
      this.sides.disabled = !engraved || !flat;
      this.sides.required = engraved && flat;
      this.confirmation.disabled = !engraved;
      this.confirmation.required = engraved;

      this.querySelectorAll('[data-rd-line-input]').forEach((input) => {
        const side = Number(input.dataset.side);
        const line = Number(input.dataset.line);
        const lineIsAvailable = line <= Number(this.config.lines);
        const sideIsAvailable = side === 1 || (side === 2 && twoSides);
        const enabled = engraved && lineIsAvailable && sideIsAvailable;
        const row = input.closest('[data-rd-line-row]');

        row.hidden = !lineIsAvailable || (side === 2 && !twoSides);
        input.disabled = !enabled;
        input.required = enabled && line === 1;
        input.maxLength = Number(this.config.characters);
        if (!enabled) input.setCustomValidity('');
        this.updateCounter(input);
      });

      if (!engraved) {
        this.confirmation.checked = false;
        this.clearError();
      }

      if (this.previewSideControls) this.previewSideControls.hidden = !twoSides;
      this.updatePreview();
    }

    updatePreview() {
      if (!this.preview || !this.config) return;

      const flat = Boolean(this.config.flat);
      const twoSides = flat && this.sides?.value === 'Two sides';
      const activeSide = twoSides ? this.previewSide : 1;
      const engraved = this.choice?.value === 'Engraved';
      const blank = this.choice?.value === 'Blank tag';
      const inputs = Array.from(
        this.querySelectorAll(`[data-rd-line-input][data-side="${activeSide}"]`)
      ).filter((input) => Number(input.dataset.line) <= Number(this.config.lines));
      const values = inputs.map((input) => input.value.trim());
      const hasText = engraved && values.some(Boolean);
      const previewSize = this.config.previewSize || 'medium';
      const previewMetrics = {
        small: { base: 4.8, width: 34 },
        medium: { base: 6, width: 44 },
        large: { base: 7.2, width: 52 },
        'flat-bone': { base: 6.4, width: 76 },
        'flat-circle': { base: 6.4, width: 58 },
      };
      const metrics = previewMetrics[previewSize] || previewMetrics.medium;
      const longestLine = Math.max(4, ...values.map((value) => value.length));
      const fittedSize = Math.min(metrics.base, metrics.width / (longestLine * 0.68));

      this.preview.style.setProperty('--rd-preview-font-size', `${fittedSize.toFixed(2)}cqi`);

      this.previewLines.forEach((line, index) => {
        const available = index < Number(this.config.lines);
        line.hidden = !available;
        line.textContent = available ? values[index] || '' : '';
      });

      if (this.previewText) this.previewText.hidden = !hasText;
      if (this.previewEmpty) {
        this.previewEmpty.hidden = hasText;
        if (blank) {
          this.previewEmpty.textContent = 'Blank tag selected';
        } else if (!engraved) {
          this.previewEmpty.textContent = 'Choose engraving to begin';
        } else {
          this.previewEmpty.textContent =
            activeSide === 2 ? 'Enter Side 2 text to preview' : 'Your engraving will appear here';
        }
      }
      if (this.previewSideLabel) {
        this.previewSideLabel.textContent = flat ? `Side ${activeSide}` : 'Stainless-steel reverse';
      }
      this.previewSideButtons.forEach((button) => {
        const selected = Number(button.dataset.rdPreviewSideButton) === activeSide;
        button.setAttribute('aria-pressed', String(selected));
        button.classList.toggle('button--primary', selected);
        button.classList.toggle('button--secondary', !selected);
      });
    }

    updateCounter(input) {
      const counter = input.closest('[data-rd-line-row]')?.querySelector('[data-rd-counter]');
      if (!counter || !this.config) return;
      const remaining = Number(this.config.characters) - input.value.length;
      counter.textContent = `${remaining} character${Math.abs(remaining) === 1 ? '' : 's'} remaining`;
      counter.classList.toggle('red-dingo-tag-counter--error', remaining < 0);
      input.setCustomValidity(remaining < 0 ? `Use no more than ${this.config.characters} characters.` : '');
    }

    activeRequiredFields() {
      return Array.from(this.querySelectorAll('[required]:not([disabled])'));
    }

    openDialog() {
      if (!this.dialog) return;
      if (typeof this.dialog.showModal === 'function') {
        if (!this.dialog.open) this.dialog.showModal();
      } else {
        this.dialog.setAttribute('open', '');
      }
      document.documentElement.classList.add('red-dingo-modal-open');
      requestAnimationFrame(() => {
        const target = this.choice?.value
          ? this.querySelector('[data-rd-line-input]:not([disabled])') || this.choice
          : this.choice;
        target?.focus?.({ preventScroll: true });
      });
    }

    closeDialog() {
      if (!this.dialog) return;
      if (typeof this.dialog.close === 'function' && this.dialog.open) {
        this.dialog.close();
      } else {
        this.dialog.removeAttribute('open');
      }
      document.documentElement.classList.remove('red-dingo-modal-open');
    }

    savePersonalisation() {
      if (!this.validate({ requireSaved: false })) return;
      this.personalisationSaved = true;
      this.updateLauncherStatus();
      this.closeDialog();
      this.openButton?.focus?.({ preventScroll: true });
    }

    updateLauncherStatus() {
      if (!this.launchStatus || !this.openLabel) return;
      if (!this.personalisationSaved) {
        this.launchStatus.hidden = true;
        this.launchStatus.textContent = '';
        this.openLabel.textContent = 'Personalise this tag';
        return;
      }

      this.launchStatus.hidden = false;
      this.launchStatus.textContent =
        this.choice?.value === 'Blank tag' ? 'Blank tag selected.' : 'Engraving details saved.';
      this.openLabel.textContent = 'Edit personalisation';
    }

    validate({ requireSaved = true } = {}) {
      const invalidField = this.activeRequiredFields().find((field) => !field.checkValidity());
      const tooLongField = Array.from(this.querySelectorAll('[data-rd-line-input]:not([disabled])')).find(
        (field) => !field.checkValidity()
      );
      const firstInvalid = invalidField || tooLongField;

      if (!firstInvalid && (!requireSaved || this.personalisationSaved)) {
        this.clearError();
        return true;
      }

      this.openDialog();

      if (!firstInvalid) {
        this.showError('Please save your personalisation before adding this tag.');
        requestAnimationFrame(() => this.saveButton?.focus?.({ preventScroll: true }));
        return false;
      }

      this.showError(
        this.choice.value
          ? 'Please complete the required engraving fields and check the character limits.'
          : 'Please choose engraving or a blank tag before adding this product.'
      );
      requestAnimationFrame(() => {
        firstInvalid.reportValidity?.();
        firstInvalid.focus?.({ preventScroll: true });
      });
      return false;
    }

    validateForSpark() {
      return this.validate();
    }

    handleSubmit(event) {
      if (this.validate()) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    }

    collectAttributes() {
      return Array.from(this.querySelectorAll('[data-rd-attribute-key]:not([disabled])'))
        .filter((field) => field.type !== 'checkbox' || field.checked)
        .map((field) => ({ key: field.dataset.rdAttributeKey, value: String(field.value || '').trim() }))
        .filter((attribute) => attribute.key && attribute.value);
    }

    showError(message) {
      if (!this.error) return;
      this.error.textContent = message;
      this.error.hidden = false;
    }

    clearError() {
      if (!this.error) return;
      this.error.textContent = '';
      this.error.hidden = true;
    }
  }

  customElements.define('red-dingo-engraving', RedDingoEngraving);
})();
